import { Link, useLocation } from "wouter";
import { useCurrentPlayer } from "@/hooks/useCurrentPlayer";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { Menu, X } from "lucide-react";
import { useState } from "react";

export function Nav() {
  const { player, isLoggedIn } = useCurrentPlayer();
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const links = [
    { href: "/tournaments", label: "Tournaments" },
    { href: "/venues", label: "Venues" },
    { href: "/leaderboard", label: "Leaderboard" },
    { href: "/host-your-venue", label: "Host Your Venue" },
  ];

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <div className="flex items-center">
            <Link href="/" className="flex items-center">
              <span
                className="text-base sm:text-xl font-extrabold tracking-tight uppercase whitespace-nowrap bg-gradient-to-b from-amber-200 via-primary to-amber-600 bg-clip-text text-transparent transition-all duration-300 hover:brightness-110"
                style={{
                  filter:
                    "drop-shadow(0 0 3px rgba(255,215,120,0.7)) drop-shadow(0 0 10px rgba(212,175,55,0.55)) drop-shadow(0 0 22px rgba(212,175,55,0.35)) drop-shadow(0 1px 0 rgba(0,0,0,0.45))",
                }}
              >
                Interclub Poker Circuit
              </span>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <div className="flex space-x-6">
              {links.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    location.startsWith(link.href) ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="flex items-center space-x-4 border-l border-border pl-6">
              <ThemeToggle />
              {isLoggedIn ? (
                <Link href="/me">
                  <Button variant="secondary" className="gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/20 text-primary text-xs font-bold uppercase">
                      {player?.name.charAt(0)}
                    </div>
                    {player?.name.split(" ")[0]}
                  </Button>
                </Link>
              ) : (
                <>
                  <Link href="/login">
                    <Button variant="ghost" className="text-muted-foreground hover:text-primary">Sign In</Button>
                  </Link>
                  <Link href="/signup">
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Join Circuit</Button>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center gap-1 md:hidden">
            <ThemeToggle />
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)} className="text-muted-foreground hover:text-primary">
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden border-b border-border bg-background px-4 pt-2 pb-4 space-y-1">
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={`block rounded-md px-3 py-2 text-base font-medium ${
                location.startsWith(link.href)
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-primary"
              }`}
              onClick={() => setIsOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <div className="mt-4 border-t border-border pt-4">
            {isLoggedIn ? (
              <Link
                href="/me"
                className="block rounded-md px-3 py-2 text-base font-medium text-muted-foreground hover:bg-muted hover:text-primary"
                onClick={() => setIsOpen(false)}
              >
                My Dashboard
              </Link>
            ) : (
              <div className="flex flex-col space-y-2 px-3">
                <Link href="/login" onClick={() => setIsOpen(false)}>
                  <Button variant="outline" className="w-full justify-center">Sign In</Button>
                </Link>
                <Link href="/signup" onClick={() => setIsOpen(false)}>
                  <Button className="w-full justify-center bg-primary text-primary-foreground">Join Circuit</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
